import * as React from 'react'
import { CalendarIcon } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { MONTHS_FULL, MONTHS_SHORT } from '@/shared/ui/calendar-grid'

interface MonthPickerInputProps {
  id?: string
  value: string // YYYY-MM
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Set false where a month is required and an empty selection has no meaning. */
  clearable?: boolean
}

function parseYearMonth(value: string): { year: number; month: number } | undefined {
  if (!value) return undefined
  const [y, m] = value.split('-').map(Number)
  if (!y || !m) return undefined
  return { year: y, month: m - 1 } // month is 0-indexed
}

function formatYearMonth(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

export function MonthPickerInput({
  id,
  value,
  onChange,
  placeholder = '—',
  disabled,
  className,
  clearable = true,
}: MonthPickerInputProps) {
  const [open, setOpen] = React.useState(false)
  const parsed = parseYearMonth(value)
  const today = React.useMemo(() => new Date(), [])

  const [viewYear, setViewYear] = React.useState(() => parsed?.year ?? today.getFullYear())

  React.useEffect(() => {
    if (open) {
      setViewYear(parsed?.year ?? today.getFullYear())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSelect = (month: number, year: number = viewYear) => {
    onChange(formatYearMonth(year, month))
    setOpen(false)
  }

  const formatted = parsed ? `${MONTHS_FULL[parsed.month]} ${parsed.year}` : null

  const thisMonth = today.getMonth()
  const thisYear = today.getFullYear()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-start text-left font-normal',
            !parsed && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          {formatted ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-3"
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
      >
        <div className="space-y-3">
          {/* Year header */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="rounded p-1 hover:bg-accent"
              aria-label="Previous year"
            >
              ‹
            </button>
            <span className="text-sm font-medium">{viewYear}</span>
            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              className="rounded p-1 hover:bg-accent"
              aria-label="Next year"
            >
              ›
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-4 gap-1">
            {MONTHS_SHORT.map((label, i) => {
              const isSelected = parsed?.year === viewYear && parsed?.month === i
              const isCurrentMonth = viewYear === thisYear && i === thisMonth
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleSelect(i)}
                  className={cn(
                    'rounded-md py-1.5 text-sm transition-colors',
                    isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                    !isSelected && isCurrentMonth && 'border border-primary text-primary',
                    !isSelected && !isCurrentMonth && 'hover:bg-accent',
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/* Actions */}
          <div className={cn('flex border-t pt-2', clearable ? 'justify-between' : 'justify-end')}>
            {clearable && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                }}
              >
                Clear
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleSelect(thisMonth, thisYear)}
            >
              This month
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
